import React, { useState, useRef, useCallback } from 'react'
import styles from './styles.module.css'

/**
 * Return type for the Cell renderer function
 */
export interface CellRenderResult {
  /** The content to display in the cell */
  value: React.ReactNode
  /** Optional tooltip text to show on hover */
  tooltip?: string
  /** Optional text color for the cell */
  color?: string
  /** Optional bold styling for the cell text */
  bold?: boolean
  /** Optional background color for the cell */
  backgroundColor?: string
}

/**
 * Column definition for the Dashboard component.
 * Similar to react-table's Column type but simplified for dashboard use.
 */
export interface DashboardColumn<T> {
  /** The header text displayed in the column */
  Header: string
  /** Tooltip text to display on hover (optional) */
  tooltip?: string
  /** Function to access the value from the data row */
  accessor: keyof T | ((row: T) => React.ReactNode)
  /** Custom cell renderer that returns value, tooltip, and color (optional) */
  Cell?: (props: { row: T; value: React.ReactNode }) => CellRenderResult
  /** Text alignment for the column (optional, defaults to 'left') */
  align?: 'left' | 'center' | 'right'
}

interface TooltipState {
  visible: boolean
  text: string
  x: number
  y: number
}

/**
 * Props for the Dashboard component
 */
interface DashboardProps<T> {
  /** Column definitions */
  columns: DashboardColumn<T>[]
  /** Data array to display */
  data: T[]
  /** Loading state */
  isLoading?: boolean
  /** Custom message when no data is available */
  noDataMessage?: string
  /** Optional totals row to display at the bottom */
  totals?: React.ReactNode[]
  /** Optional totals row label (defaults to 'Total') */
  totalsLabel?: string
  /** Optional key extractor for rows (defaults to index) */
  getRowKey?: (row: T, index: number) => string | number
  /** Optional max height for the dashboard container */
  maxHeight?: string
  /** Optional callback when a row is clicked */
  onRowClick?: (row: T) => void
}

function DashboardIndicator({
  isLoading,
  noDataMessage,
  colSpan,
}: {
  isLoading: boolean
  noDataMessage: string
  colSpan: number
}) {
  return (
    <tr>
      <td colSpan={colSpan} className={styles.loadingCell}>
        {isLoading ? 'Loading...' : noDataMessage}
      </td>
    </tr>
  )
}

/**
 * A reusable Dashboard component for displaying data in a striped table format.
 * Similar API to the Table component but designed for dashboard/summary views
 * without pagination.
 *
 * @example
 * ```tsx
 * interface DefectData {
 *   typeDisplayName: string
 *   count: number
 *   totalDamageCost: number
 * }
 *
 * const columns: DashboardColumn<DefectData>[] = [
 *   { Header: 'Defect Type', accessor: 'typeDisplayName' },
 *   {
 *     Header: 'Count',
 *     accessor: 'count',
 *     align: 'center',
 *     Cell: ({ row, value }) => ({
 *       value: value,
 *       tooltip: (value as number) > 10 ? `High count for ${row.typeDisplayName}` : undefined,
 *       color: (value as number) > 10 ? 'red' : undefined,
 *     }),
 *   },
 *   {
 *     Header: 'Total Damage Cost',
 *     accessor: 'totalDamageCost',
 *     align: 'right',
 *     Cell: ({ value }) => ({
 *       value: formatCurrency(value as number),
 *     }),
 *   },
 * ]
 *
 * <Dashboard
 *   columns={columns}
 *   data={defectsData}
 *   totals={[totalCount, formatCurrency(totalDamageCost)]}
 * />
 * ```
 */
function Dashboard<T>({
  columns,
  data,
  isLoading = false,
  noDataMessage = 'No data available.',
  totals,
  totalsLabel = 'Total',
  getRowKey,
  maxHeight = '75vh',
  onRowClick,
}: DashboardProps<T>) {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    text: '',
    x: 0,
    y: 0,
  })
  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>, tooltipText: string) => {
      const rect = e.currentTarget.getBoundingClientRect()
      setTooltip({
        visible: true,
        text: tooltipText,
        x: rect.left + rect.width / 2,
        y: rect.top,
      })
    },
    [],
  )

  const handleMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }))
  }, [])

  const getCellValue = (
    row: T,
    accessor: DashboardColumn<T>['accessor'],
  ): React.ReactNode => {
    if (typeof accessor === 'function') {
      return accessor(row)
    }
    return row[accessor] as React.ReactNode
  }

  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return styles.alignCenter
      case 'right':
        return styles.alignRight
      default:
        return styles.alignLeft
    }
  }

  const shouldShowIndicator = isLoading || data.length === 0

  return (
    <div className={styles.dashboardWrapper}>
      {/* Fixed position tooltip rendered outside the scrollable container */}
      {tooltip.visible && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div className={styles.dashboardTableContainer} style={{ maxHeight }}>
        <table className={styles.dashboardTable}>
          <thead>
          <tr>
            {columns.map((column, idx) => (
              <th
                key={idx}
                className={`${styles.headerCell} ${getAlignmentClass(column.align)} ${
                  column.tooltip ? styles.headerCellWithTooltip : ''
                }`}
                onMouseEnter={
                  column.tooltip
                    ? (e) => handleMouseEnter(e, column.tooltip!)
                    : undefined
                }
                onMouseLeave={column.tooltip ? handleMouseLeave : undefined}
              >
                {column.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shouldShowIndicator ? (
            <DashboardIndicator
              isLoading={isLoading}
              noDataMessage={noDataMessage}
              colSpan={columns.length}
            />
          ) : (
            <>
              {data.map((row, rowIdx) => {
                const rowKey = getRowKey ? getRowKey(row, rowIdx) : rowIdx
                return (
                  <tr
                    key={rowKey}
                    className={`${styles.dataRow} ${onRowClick ? styles.clickableRow : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((column, colIdx) => {
                      const rawValue = getCellValue(row, column.accessor)
                      const cellResult = column.Cell
                        ? column.Cell({ row, value: rawValue })
                        : { value: rawValue }
                      const {
                        value: cellContent,
                        tooltip: cellTooltip,
                        color: cellColor,
                        bold: cellBold,
                        backgroundColor: cellBackgroundColor,
                      } = cellResult

                      const cellStyle: React.CSSProperties = {}
                      if (cellColor) cellStyle.color = cellColor
                      if (cellBold) cellStyle.fontWeight = 'bold'
                      if (cellBackgroundColor) cellStyle.backgroundColor = cellBackgroundColor

                      return (
                        <td
                          key={colIdx}
                          className={`${styles.dataCell} ${getAlignmentClass(column.align)} ${
                            colIdx === 0 ? styles.firstColumnCell : ''
                          } ${cellTooltip ? styles.cellWithTooltip : ''}`}
                          style={Object.keys(cellStyle).length > 0 ? cellStyle : undefined}
                          onMouseEnter={
                            cellTooltip
                              ? (e) => handleMouseEnter(e, cellTooltip)
                              : undefined
                          }
                          onMouseLeave={cellTooltip ? handleMouseLeave : undefined}
                        >
                          {cellContent}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
              {totals && (
                <tr className={styles.totalsRow}>
                  <td className={`${styles.totalsCell} ${styles.firstColumnCell}`}>
                    {totalsLabel}
                  </td>
                  {totals.map((totalValue, idx) => (
                    <td
                      key={idx}
                      className={`${styles.totalsCell} ${getAlignmentClass(columns[idx + 1]?.align)}`}
                    >
                      {totalValue}
                    </td>
                  ))}
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}

export default Dashboard
